// API Service for authentication
const API_URL = 'https://qbczwjf0sb.execute-api.us-east-1.amazonaws.com/prod';
const API_KEY_SECURE = 'GZnDGWEfXsSqtABHVD411yVY5UarxE88sMWYOMz';
const PHONE_HEADER_NAME = 'X-Phone-Number';
const USE_MOCK_DATA = false;

// Mock data fallback (for development)
import { getFamilyByPhone, validatePhoneNumber as mockValidatePhone } from '../data/mockData';

/**
 * Persist `year` query parameter for the current session.
 * Uses sessionStorage so it lasts for the tab's lifetime.
 */
function persistYearFromQuery() {
  try {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    const year = params.get('year');
    if (year) {
      sessionStorage.setItem('y', year);
      //set real API key
      API_KEY = `${API_KEY_SECURE}1${year}${year}`;
    }
  } catch (err) {
    console.warn('Unable to persist year from query:', err);
  }
}

/**
 * Get a query string parameter by name and log it.
 * Defaults to 'year' if no name is provided.
 * @param {string} [name='year']
 * @returns {string|null}
 */
export function GetQueryStringParameters(name = 'year') {
  if (typeof window === 'undefined') {
    console.log(`GetQueryStringParameters: window not available`);
    return null;
  }
  const params = new URLSearchParams(window.location.search || '');
  const value = params.get(name);
  console.log(`Query param ${name}:`, value);
  return value;
}

// Run once on module load to persist the year for this session
persistYearFromQuery();

/**
 * Make API request with proper headers
 */
async function makeApiRequest(path, payload) {
  const url = `${API_URL}${path}`;
  const API_KEY = `${API_KEY_SECURE}1${sessionStorage.getItem('y')}${sessionStorage.getItem('y')}`;
  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    [PHONE_HEADER_NAME]: payload.phone || '',
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = { message: await response.text() };
    }

    return {
      success: response.ok,
      status: response.status,
      data
    };
  } catch (error) {
    console.error('API Request Error:', error);
    return {
      success: false,
      status: 0,
      data: { error: 'Error occurred. Please make sure you are using the FULLY correct URL from the QR code or the website (hint: it should have a "year" in it)' }
    };
  }
}

/**
 * Request OTP for phone number with specified method
 * @param {string} phone - Phone number (formatted or raw)
 * @param {string} method - "sms" or "call" (will be mapped to "sms" or "call")
 */
export async function requestOtp(phone, method) {
  if (USE_MOCK_DATA) {
    // Mock behavior - always succeed for valid phone numbers
    const isValid = mockValidatePhone(phone);
    return {
      success: isValid,
      data: isValid ? { message: 'OTP sent successfully' } : { error: 'Invalid phone number' }
    };
  }

  // Clean phone number - remove formatting
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Map UI method to API method
  const apiMethod = method === 'call' ? 'call' : 'sms';
  
  const result = await makeApiRequest('/request-otp', {
    phone: cleanPhone,
    method: apiMethod
  });

  return result;
}

/**
 * Verify OTP code
 * @param {string} phone - Phone number (formatted or raw)
 * @param {string} otp - 6-digit OTP code
 */
export async function verifyOtp(phone, otp) {
  if (USE_MOCK_DATA) {
    // Mock behavior - only accept 123456
    const isValid = otp === '123456';
    if (isValid) {
      const familyData = getFamilyByPhone(phone);
      return {
        success: true,
        data: { message: 'OTP verified successfully', familyData }
      };
    } else {
      return {
        success: false,
        data: { error: 'Invalid OTP code' }
      };
    }
  }

  // Clean phone number - remove formatting
  const cleanPhone = phone.replace(/\D/g, '');
  
  const result = await makeApiRequest('/verify-otp', {
    phone: cleanPhone,
    otp: otp
  });

  // If verification successful, get family data (you'll need to implement this endpoint)
  if (result.success) {
    // const familyData = getFamilyByPhone(phone);
    result.data.familyData = result.data.data.family[0];
  }

  return result;
}

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 */
export function validatePhoneNumber(phone) {
  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, '');
  
  // US phone number should be exactly 10 digits
  return cleaned.length === 10;
}

/**
 * Check if app is using mock data
 */
export function isUsingMockData() {
  return USE_MOCK_DATA;
}
